import { FeatureDefinition, type FeatureDefinitionSnapshot } from '../FeatureDefinition';

/**
 * 圆角特征定义快照。
 */
export interface FilletFeatureDefinitionSnapshot extends FeatureDefinitionSnapshot {
    /** 当前特征种类。 */
    featureKind: 'fillet';
    /** 原始命令参数。 */
    commandParams: FilletParams;
}

/**
 * 圆角特征定义投影。
 */
export interface FilletFeatureDefinitionProjection {
    /** 上游实体对象 ID。 */
    upstreamBodyObjectId: string | null;
    /** 结果对象 ID。 */
    resultObjectId: string | null;
    /** 圆角定义。 */
    definition: FilletFeatureDefinition;
    /** 特征名称。 */
    name: string | null;
}

/**
 * 圆角特征定义对象。
 */
export class FilletFeatureDefinitionModel extends FeatureDefinition<FilletFeatureDefinitionSnapshot> {
    /**
     * 基于命令参数创建圆角定义对象。
     *
     * @param input 输入参数。
     * @returns 圆角定义对象。
     */
    public static fromParams(input: {
        commandParams: FilletParams;
        version?: number;
    }): FilletFeatureDefinitionModel {
        return new FilletFeatureDefinitionModel({
            featureKind: 'fillet',
            version: input.version ?? 1,
            commandParams: structuredClone(input.commandParams),
        });
    }

    /**
     * @param snapshot 定义快照。
     */
    public constructor(snapshot: FilletFeatureDefinitionSnapshot) {
        super(snapshot);
    }

    /**
     * 获取原始命令参数。
     *
     * @returns 命令参数快照。
     */
    public getCommandParams(): FilletParams {
        return this.toSnapshot().commandParams;
    }

    /**
     * 获取圆角领域定义。
     *
     * @returns 圆角定义快照。
     */
    public getBlendDefinition(): FilletFeatureDefinition {
        return structuredClone(this.toSnapshot().commandParams.definition);
    }

    /**
     * 获取上游实体对象 ID。
     *
     * @returns 上游实体对象 ID。
     */
    public getUpstreamBodyObjectId(): string | null {
        return typeof this.toSnapshot().commandParams.upstreamBodyObjectId === 'string'
            ? this.toSnapshot().commandParams.upstreamBodyObjectId
            : null;
    }

    /**
     * 生成面向 UI 的投影快照。
     *
     * @returns 投影快照。
     */
    public toProjection(): FilletFeatureDefinitionProjection {
        const commandParams = this.toSnapshot().commandParams;
        return {
            upstreamBodyObjectId: typeof commandParams.upstreamBodyObjectId === 'string'
                ? commandParams.upstreamBodyObjectId
                : null,
            resultObjectId: typeof commandParams.resultObjectId === 'string'
                ? commandParams.resultObjectId
                : null,
            definition: structuredClone(commandParams.definition),
            name: typeof commandParams.name === 'string' ? commandParams.name : null,
        };
    }

    /**
     * 基于新命令参数生成下一份定义对象。
     *
     * @param commandParams 新命令参数。
     * @returns 新定义对象。
     */
    public withCommandParams(commandParams: FilletParams): FilletFeatureDefinitionModel {
        return FilletFeatureDefinitionModel.fromParams({
            commandParams,
            version: this.getVersion(),
        });
    }
}
